"use client";

/**
 * SSR-friendly Framer Motion wrappers.
 * Server Components render children into these wrappers so all text/images
 * stay in the initial HTML payload while animations hydrate on the client.
 */

import {
  motion,
  type HTMLMotionProps,
  type TargetAndTransition,
  type Transition,
  type VariantLabels,
} from "framer-motion";
import type { ReactNode } from "react";

type AnimateInProps = HTMLMotionProps<"div"> & {
  /** Optional so decorative, childless elements (lines, dots) can animate too. */
  children?: ReactNode;
};

/** Fade / slide in when scrolled into view (default: once). */
export function AnimateIn({
  children,
  initial = { opacity: 0, y: 20 },
  whileInView = { opacity: 1, y: 0 },
  viewport = { once: true },
  transition = { duration: 0.6 },
  ...rest
}: AnimateInProps) {
  return (
    <motion.div
      initial={initial}
      whileInView={whileInView}
      viewport={viewport}
      transition={transition}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

type AnimateInArticleProps = HTMLMotionProps<"article"> & {
  children: ReactNode;
};

/** Same as {@link AnimateIn} but keeps the semantic `<article>` element. */
export function AnimateInArticle({
  children,
  initial = { opacity: 0, y: 20 },
  whileInView = { opacity: 1, y: 0 },
  viewport = { once: true },
  transition = { duration: 0.6 },
  ...rest
}: AnimateInArticleProps) {
  return (
    <motion.article
      initial={initial}
      whileInView={whileInView}
      viewport={viewport}
      transition={transition}
      {...rest}
    >
      {children}
    </motion.article>
  );
}

type AnimateInAsideProps = HTMLMotionProps<"aside"> & {
  children: ReactNode;
};

/** Same as {@link AnimateIn} but keeps the semantic `<aside>` element. */
export function AnimateInAside({
  children,
  initial = { opacity: 0, y: 20 },
  whileInView = { opacity: 1, y: 0 },
  viewport = { once: true },
  transition = { duration: 0.6 },
  ...rest
}: AnimateInAsideProps) {
  return (
    <motion.aside
      initial={initial}
      whileInView={whileInView}
      viewport={viewport}
      transition={transition}
      {...rest}
    >
      {children}
    </motion.aside>
  );
}

type AnimateOnMountProps = HTMLMotionProps<"div"> & {
  children?: ReactNode;
};

/** Animate on first paint (hero-style mount animations). */
export function AnimateOnMount({
  children,
  initial = { opacity: 0, y: 20 },
  animate = { opacity: 1, y: 0 },
  transition = { duration: 0.6 },
  ...rest
}: AnimateOnMountProps) {
  return (
    <motion.div
      initial={initial}
      animate={animate}
      transition={transition}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

type MotionFloatProps = {
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  animate?: TargetAndTransition | VariantLabels;
  transition?: Transition;
};

/** Decorative looping motion (background blobs, sparklers). */
export function MotionFloat({
  children,
  className,
  style,
  animate,
  transition,
}: MotionFloatProps) {
  return (
    <motion.div
      className={className}
      style={style}
      animate={animate}
      transition={transition}
      aria-hidden={children == null ? true : undefined}
    >
      {children}
    </motion.div>
  );
}

type HoverLiftProps = HTMLMotionProps<"div"> & {
  children: ReactNode;
};

export function HoverLift({
  children,
  whileHover = { y: -5 },
  whileTap,
  ...rest
}: HoverLiftProps) {
  return (
    <motion.div whileHover={whileHover} whileTap={whileTap} {...rest}>
      {children}
    </motion.div>
  );
}

export { motion };
